import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, RotateCcw, Trophy, Award } from "lucide-react";
import { toast } from "sonner";
import Panda from "../Panda";
import { useProgress } from "../../context/ProgressContext";
import { modules } from "../../data/mock";

const Quiz = ({ m }) => {
  const { completeStep, completed, panda, moduleProgress } = useProgress();
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const q = m.quiz[i];
  const best = completed[m.id]?.quiz;
  const nextMod = modules[m.num];

  const choose = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    if (idx === q.answer) setScore((s) => s + 1);
  };

  const nextQ = () => {
    if (i + 1 < m.quiz.length) {
      setI(i + 1);
      setPicked(null);
    } else {
      const finalScore = score;
      setFinished(true);
      const firstTime = best === undefined;
      completeStep(m.id, "quiz", Math.max(finalScore, best || 0));
      if (firstTime) toast.success("+10 bamboo!", { description: `Quiz finished with ${finalScore}/${m.quiz.length}` });
    }
  };

  const restart = () => { setI(0); setPicked(null); setScore(0); setFinished(false); };

  if (finished) {
    const perfect = score === m.quiz.length;
    const lessonDone = moduleProgress(m.id) === 100;
    return (
      <div className="max-w-xl mx-auto text-center bg-white rounded-3xl shadow-lg p-10" data-testid="quiz-result">
        <div className="flex justify-center"><Panda size={150} accessory={perfect ? "crown" : panda?.accessory || m.pandaAccessory} scarf={panda?.scarf || m.pandaScarf} item="coin" animate /></div>
        <Trophy className="mx-auto text-[#f3c932] mt-2" size={40} />
        <h3 className="text-3xl font-bold text-[#0c4a6e] mt-2" style={{ fontFamily: "Literata, serif" }}>{perfect ? "Perfect score!" : score >= 3 ? "Great work!" : "Good try!"}</h3>
        <p className="text-gray-600 mt-2 text-lg">You got <span className="font-bold text-[#e5356f]" data-testid="quiz-score">{score}</span> out of {m.quiz.length}.</p>
        {!perfect && <p className="text-gray-500 mt-1 text-sm">Re-read the Learn cards and try again for a perfect score.</p>}
        {lessonDone && (
          <Link to={`/certificate/${m.slug}`} className="mt-6 inline-flex items-center gap-2 bg-[#fff8e1] border-2 border-[#f3c932] text-[#8a6d00] font-bold rounded-2xl px-5 py-3 hover:bg-[#f3c932]/30 cert-pop" data-testid="quiz-certificate-link">
            <Award size={20} className="text-[#f3c932]" fill="#f3c932" /> Lesson complete - view {panda?.name ? `${panda.name}'s` : "your"} certificate
          </Link>
        )}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button onClick={restart} className="btn-pill bg-white border-2 border-[#1b8bc9] text-[#1b8bc9] hover:bg-[#e9f9fd] inline-flex items-center gap-2" data-testid="quiz-retry-button"><RotateCcw size={18} /> Try again</button>
          {nextMod ? (
            <Link to={`/lessons/${nextMod.slug}`} className="btn-pill text-white" style={{ background: nextMod.button }}>Next: {nextMod.short}</Link>
          ) : (
            <Link to="/lessons" className="btn-pill bg-[#e5356f] text-white">Back to lessons</Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="quiz">
      <div className="flex items-center justify-between mb-4 text-sm font-bold text-gray-500">
        <span>Question {i + 1} of {m.quiz.length}</span>
        {best !== undefined && <span className="text-[#2e8b57]">Best: {best}/{m.quiz.length}</span>}
      </div>
      <div className="flex gap-1.5 mb-6">
        {m.quiz.map((_, k) => <span key={k} className="h-2 flex-1 rounded-full" style={{ background: k < i ? "#2e8b57" : k === i ? m.color : "#e9f9fd" }} />)}
      </div>
      <div className="bg-white rounded-3xl shadow-lg p-8">
        <h3 className="text-2xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }} data-testid="quiz-question">{q.q}</h3>
        <div className="mt-6 grid gap-3">
          {q.options.map((o, idx) => {
            const isAnswer = idx === q.answer;
            const isPicked = picked === idx;
            let cls = "border-[#e9f9fd] hover:border-[#1b8bc9] hover:bg-[#f7fdfe]";
            if (picked !== null) {
              if (isAnswer) cls = "border-[#2e8b57] bg-[#e8f8ef]";
              else if (isPicked) cls = "border-[#e5356f] bg-[#fff0f5]";
              else cls = "border-[#e9f9fd] opacity-60";
            }
            return (
              <button
                key={idx}
                onClick={() => choose(idx)}
                disabled={picked !== null}
                className={`text-left rounded-2xl border-2 px-5 py-4 font-medium text-gray-800 flex items-center justify-between gap-3 transition-[border-color,background-color,transform] hover:-translate-y-0.5 disabled:hover:translate-y-0 ${cls}`}
                data-testid={`quiz-option-${idx}`}
              >
                <span>{o}</span>
                {picked !== null && isAnswer && <Check className="text-[#2e8b57] flex-shrink-0" />}
                {picked !== null && isPicked && !isAnswer && <X className="text-[#e5356f] flex-shrink-0" />}
              </button>
            );
          })}
        </div>
        {picked !== null && (
          <div className="mt-6 rounded-2xl p-4 bubble-in" style={{ background: picked === q.answer ? "#e8f8ef" : "#fff0f5" }} data-testid="quiz-feedback">
            <p className="font-bold" style={{ color: picked === q.answer ? "#0d4d33" : "#7a1f45" }}>{picked === q.answer ? "Correct!" : "Not quite."}</p>
            <p className="text-gray-700 mt-1">{q.explain}</p>
            <button onClick={nextQ} className="mt-4 btn-pill text-white" style={{ background: m.button }} data-testid="quiz-next-button">{i + 1 < m.quiz.length ? "Next question" : "See my score"}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;
